import { generateBidOptions, getNextBidMinimum, incrementAbsenteeBid, roundAbsenteeBid } from '@/utils/absenteeBids';
import { getBidIncrementsByItemId } from '@/redux/modules/bidIncrements/bidIncrements.selectors';
import { getItemDetailSuggestedBidFloor } from '@/redux/modules/itemDetail';
import {
    getItemModelStartPrice,
    getItemSummaryBidderMaxBid,
    getItemSummaryLeadingBid,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { useAppSelector } from '@/redux/hooks';
import { useMemo } from 'react';

const MIN_BID_OPTIONS = 3;

type Props = {
    hasReserve?: boolean;
    itemId: number;
};

/**
 * Gets an appropriate starting bid floor by rounding the suggested floor to a valid increment
 */
const getValidBidFloor = (suggestedBidFloor: number, bidIncrements: { fromAmount: number; incAmount: number }[]) => {
    if (!suggestedBidFloor || !bidIncrements?.length) {
        return 0;
    }

    // Round to the nearest valid increment, then increment by one step
    const roundedFloor = roundAbsenteeBid(bidIncrements, suggestedBidFloor);
    return incrementAbsenteeBid(bidIncrements, roundedFloor);
};

/**
 * Hook that generates the 1st, 3rd, and 5th bidding option values for an item based on bid increments and current bid state.
 * Also if the item has a reserve price, the options will be generated above the reserve price.
 *
 * @param props - The hook parameters
 * @param props.itemId - The ID of the item to generate bid options for
 * @returns An array of 3 bid amount values in the item's currency
 *
 * @example
 * ```tsx
 * const bidOptions = useGetItemBidOptions({ itemId: 123 });
 * // Returns: [110, 135, 160]
 * ```
 */
export const useGetItemBidChipSuggestions = ({ hasReserve = false, itemId }: Props) => {
    const suggestedBidFloor = useAppSelector((state) => getItemDetailSuggestedBidFloor(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId)) || 0;
    const bidIncrements = useAppSelector((state) => getBidIncrementsByItemId(state, itemId));
    const bidderMaxBid = useAppSelector((state) => getItemSummaryBidderMaxBid(state, itemId)) || 0;
    const startPrice = useAppSelector((state) => getItemModelStartPrice(state, itemId)) || 0;

    // Calculate the minimum bid amount
    const nextBidMinimum = getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0;

    // Calculate the valid bid floor for reserve items
    const validBidFloor = useMemo(
        () => getValidBidFloor(suggestedBidFloor, bidIncrements),
        [suggestedBidFloor, bidIncrements]
    );

    // Generate bid options based on whether this is a reserve item
    const isReserveItem = hasReserve && suggestedBidFloor > 0 && nextBidMinimum < validBidFloor;

    return useMemo(() => {
        if (isReserveItem) {
            // For reserve items, generate options starting from the valid floor
            return generateBidOptions(bidIncrements, Math.max(nextBidMinimum, validBidFloor), validBidFloor).slice(
                0,
                3
            );
        }

        // For non-reserve items, generate options starting from the next bid minimum
        const options = generateBidOptions(bidIncrements, nextBidMinimum, nextBidMinimum);
        if (options.length < MIN_BID_OPTIONS) {
            return [];
        }
        return [options[0], options[2], options[4]].filter(Boolean);
    }, [isReserveItem, bidIncrements, nextBidMinimum, validBidFloor]);
};
